import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ItemIssueItem, ItemIssuesState } from './types';

const initialState: ItemIssuesState = {
  items: [],
  totalCount: 0,
  error: null,
  hasNextPage: true,
  endCursor: '',
  searchQuery: '',
  selectedWarehouseIds: [],
  reviewingItemId: null,
  reviewingItemIndex: -1,
};

// Shared helpers for reducer logic
const filterItem = (state: ItemIssuesState, itemId: string) => {
  state.items = state.items.filter((item) => item.id !== itemId);
  state.totalCount = Math.max(0, state.totalCount - 1);
};

const clearReviewing = (state: ItemIssuesState) => {
  state.reviewingItemId = null;
  state.reviewingItemIndex = -1;
};

const itemIssuesSlice = createSlice({
  name: 'itemIssues',
  initialState,
  reducers: {
    setItems: (state, action: PayloadAction<ItemIssueItem[]>) => {
      state.items = action.payload;
    },
    appendItems: (state, action: PayloadAction<ItemIssueItem[]>) => {
      const existingIds = new Set(state.items.map((i) => i.id));
      const newItems = action.payload.filter((i) => !existingIds.has(i.id));
      state.items = [...state.items, ...newItems];
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setHasNextPage: (state, action: PayloadAction<boolean>) => {
      state.hasNextPage = action.payload;
    },
    setEndCursor: (state, action: PayloadAction<string>) => {
      state.endCursor = action.payload;
    },
    resetEndCursor: (state) => {
      state.endCursor = '';
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    resetSearchQuery: (state) => {
      state.searchQuery = '';
    },
    setSelectedWarehouseIds: (state, action: PayloadAction<string[]>) => {
      state.selectedWarehouseIds = action.payload;
    },
    setReviewingItem: (
      state,
      action: PayloadAction<{ id: string; index: number }>,
    ) => {
      state.reviewingItemId = action.payload.id;
      state.reviewingItemIndex = action.payload.index;
    },
    clearReviewingItem: (state) => {
      clearReviewing(state);
    },
    removeItem: (state, action: PayloadAction<string>) => {
      filterItem(state, action.payload);
    },
    // Remove item and advance to the next item at the same index
    removeItemAndAdvance: (state, action: PayloadAction<string>) => {
      filterItem(state, action.payload);
      const idx = state.reviewingItemIndex;
      if (idx < state.items.length) {
        state.reviewingItemId = state.items[idx].id;
        state.reviewingItemIndex = idx;
      } else if (state.items.length > 0) {
        const newIdx = state.items.length - 1;
        state.reviewingItemId = state.items[newIdx].id;
        state.reviewingItemIndex = newIdx;
      } else {
        clearReviewing(state);
      }
    },
    // Remove item and close the review modal
    removeItemAndClose: (state, action: PayloadAction<string>) => {
      filterItem(state, action.payload);
      clearReviewing(state);
    },
    updateItemInList: (
      state,
      action: PayloadAction<{
        id: string;
        description?: string | null;
        hasOwnImage?: boolean;
        imgUrl?: string | null;
      }>,
    ) => {
      const idx = state.items.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (idx >= 0) {
        state.items[idx] = { ...state.items[idx], ...action.payload };
      }
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    resetState: () => initialState,
  },
});

export const itemIssuesActions = itemIssuesSlice.actions;

export default itemIssuesSlice.reducer;
